'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import { LayoutDashboard, Shield, Zap, Edit3, FlaskConical, Upload, ChevronLeft, ChevronRight, Bell, Settings, LogOut, Grid3X3, Home, CalendarClock, Layers } from 'lucide-react';

interface NavItem {
  href: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
}

const navItems: NavItem[] = [
  { href: '/', label: 'Start', icon: <Home size={18} /> },
  { href: '/content-dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} />, badge: 3 },
  { href: '/brand-vault', label: 'Brand Vault', icon: <Shield size={18} /> },
  { href: '/content-generator', label: 'Generator', icon: <Zap size={18} />, badge: 1 },
  { href: '/bulk-creation', label: 'Bulk Creation', icon: <Layers size={18} /> },
  { href: '/backstage', label: 'Backstage Grid', icon: <Grid3X3 size={18} /> },
  { href: '/content-editor', label: 'Editor', icon: <Edit3 size={18} /> },
  { href: '/scheduler', label: 'Scheduler', icon: <CalendarClock size={18} /> },
  { href: '/visual-lab', label: 'Visual Lab', icon: <FlaskConical size={18} /> },
  { href: '/export-sync', label: 'Export & Sync', icon: <Upload size={18} /> },
];

interface SidebarProps {
  currentPath: string;
}

export default function Sidebar({ currentPath }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();

  const displayName = user?.user_metadata?.full_name || 'Rebel Explorer';
  const displayEmail = user?.email || '';
  const avatarInitial = displayName.charAt(0).toUpperCase();

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/80 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile toggle button */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="fixed top-4 left-4 z-50 lg:hidden p-2 bg-surface border border-border rounded"
        aria-label="Menü öffnen"
      >
        <div className="w-5 h-0.5 bg-white mb-1" />
        <div className="w-5 h-0.5 bg-white mb-1" />
        <div className="w-5 h-0.5 bg-white" />
      </button>

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:relative z-50 lg:z-auto
          h-full flex flex-col
          bg-black border-r border-border
          transition-all duration-300 ease-in-out
          ${collapsed ? 'w-16' : 'w-60'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        aria-label="Seitennavigation"
      >
        {/* Logo */}
        <div className={`flex items-center border-b border-border h-14 px-3 ${collapsed ? 'justify-center' : 'justify-between'}`}>
          {!collapsed && (
            <div className="flex items-center gap-2 min-w-0">
              <AppLogo size={28} />
              <div className="min-w-0">
                <span className="font-display font-800 text-xs tracking-widest text-accent uppercase block leading-none">
                  SEARCH4
                </span>
                <span className="font-display font-700 text-xs tracking-widest text-white uppercase block leading-none">
                  DISTORTION
                </span>
              </div>
            </div>
          )}
          {collapsed && (
            <AppLogo size={28} />
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex p-1 text-muted hover:text-white transition-colors ml-auto"
            title={collapsed ? 'Menü erweitern' : 'Menü einklappen'}
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto" aria-label="Hauptnavigation">
          {!collapsed && (
            <p className="section-label px-3 pb-2">Navigation</p>
          )}
          {navItems.map((item) => {
            const isActive = item.href === '/'
              ? currentPath === '/'
              : currentPath === item.href || currentPath.startsWith(item.href + '/');
            return (
              <Link
                key={`nav-${item.href}`}
                href={item.href}
                className={`nav-item ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-2' : ''}`}
                title={collapsed ? item.label : undefined}
                onClick={() => setMobileOpen(false)}
              >
                <span className={`flex-shrink-0 ${isActive ? 'text-accent' : ''}`}>
                  {item.icon}
                </span>
                {!collapsed && (
                  <span className="flex-1 truncate">{item.label}</span>
                )}
                {!collapsed && item.badge && (
                  <span className="ml-auto bg-accent text-black text-xs font-700 mono rounded px-1.5 py-0.5 min-w-[18px] text-center leading-none">
                    {item.badge}
                  </span>
                )}
                {collapsed && item.badge && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-accent rounded-full" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="border-t border-border p-2 space-y-1">
          {!collapsed && (
            <button className="nav-item w-full">
              <Bell size={16} />
              <span>Benachrichtigungen</span>
              <span className="ml-auto bg-surface-2 text-muted text-xs mono rounded px-1.5 py-0.5">3</span>
            </button>
          )}
          <Link
            href="/settings"
            className={`nav-item w-full ${collapsed ? 'justify-center px-2' : ''} ${currentPath === '/settings' ? 'active' : ''}`}
            title={collapsed ? 'Einstellungen' : undefined}
          >
            <Settings size={16} />
            {!collapsed && <span>Einstellungen</span>}
          </Link>
          <div className={`flex items-center gap-3 px-3 py-2 mt-2 border-t border-border pt-3 ${collapsed ? 'justify-center' : ''}`}>
            <div className="w-7 h-7 rounded bg-accent/20 border border-accent/40 flex items-center justify-center flex-shrink-0">
              <span className="text-accent text-xs font-semibold">{avatarInitial}</span>
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-xs font-600 text-white truncate">{displayName}</p>
                <p className="text-xs text-muted truncate mono">{displayEmail}</p>
              </div>
            )}
            {!collapsed && (
              <Link href="/login" className="text-muted hover:text-white transition-colors" title="Abmelden">
                <LogOut size={14} />
              </Link>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}